import { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { useAuth } from "@/components/layout/AuthProvider"

interface P { user: any; onMenuToggle: () => void }

export function Header({ user, onMenuToggle }: P) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [search, setSearch] = useState("")
  const menuRef = useRef<HTMLDivElement>(null)
  const nav = useNavigate()
  const { signOut } = useAuth()

  useEffect(() => {
    const fn = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false) }
    document.addEventListener("mousedown", fn)
    return () => document.removeEventListener("mousedown", fn)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (search.trim()) { nav(`/search?q=${encodeURIComponent(search.trim())}`); setSearch("") }
  }

  const handleSignOut = async () => {
    setMenuOpen(false)
    await signOut()
    nav("/auth/login")
  }

  return (
    <header className="app-header px-3 gap-2 sm:gap-3">
      <button onClick={onMenuToggle} className="lg:hidden sq-btn-ghost px-2 flex-shrink-0" aria-label="Menu">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" /></svg>
      </button>

      <Link to="/feed" className="flex items-center gap-2 flex-shrink-0">
        <svg width="26" height="26" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="8" fill="#ff4500" />
          <path d="M15 17.5C15 14.462 17.462 12 20.5 12H29C30.105 12 31 12.895 31 14C31 15.105 30.105 16 29 16H20.5C19.672 16 19 16.672 19 17.5C19 18.328 19.672 19 20.5 19H27.5C30.538 19 33 21.462 33 24.5C33 27.538 30.538 30 27.5 30H19C17.895 30 17 30.895 17 32C17 33.105 17.895 34 19 34H28.5C29.328 34 30 34.672 30 35.5C30 36.328 29.328 37 28.5 37H19C16.239 37 14 34.761 14 32C14 29.239 16.239 27 19 27H27.5C28.328 27 29 26.328 29 25.5C29 24.672 28.328 24 27.5 24H20.5C17.462 24 15 21.538 15 18.5V17.5Z" fill="white" />
        </svg>
        <span className="sq-logo text-[1.15rem] hidden sm:block">SQUIDS</span>
      </Link>

      <form onSubmit={handleSearch} className="flex-1 max-w-[500px] mx-2 hidden sm:flex">
        <div className="search-bar w-full">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "var(--text3)" }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SQUIDS" className="bg-transparent flex-1 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
      </form>

      <div className="flex items-center gap-2 ml-auto">
        <Link to="/search" className="sm:hidden sq-btn-ghost px-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        </Link>

        {user ? (
          <>
            <Link to="/post/create" className="sq-btn sq-btn-primary text-xs px-3 py-1.5 hidden sm:flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
              Post
            </Link>
            <div ref={menuRef} className="relative">
              <button onClick={() => setMenuOpen(v => !v)} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-[var(--bg3)] transition-colors">
                <div className="w-7 h-7 rounded-full overflow-hidden bg-[var(--bg4)] flex-shrink-0">
                  {user.avatar_url
                    ? <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
                    : <AvatarFallback name={user.display_name} />}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-xs font-bold leading-none truncate max-w-[80px]">{user.display_name}</p>
                  <p className="text-xs leading-none mt-0.5" style={{ color: "var(--text2)" }}>{(user.karma_score || 0).toLocaleString()} karma</p>
                </div>
                <svg className="w-3 h-3 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "var(--text3)" }}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: .97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: .97 }}
                    transition={{ duration: .1 }}
                    className="absolute right-0 top-full mt-1 w-56 rounded-lg shadow-2xl overflow-hidden z-50"
                    style={{ background: "var(--bg2)", border: "1px solid var(--border2)" }}
                  >
                    <div className="px-3 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
                      <p className="text-sm font-bold truncate">{user.display_name}</p>
                      <p className="text-xs truncate" style={{ color: "var(--text2)" }}>u/{user.username}</p>
                    </div>
                    <div className="py-1">
                      {[
                        { to: `/profile/${user.username}`, l: "Profile" },
                        { to: "/dashboard", l: "Dashboard" },
                        { to: "/bookmarks", l: "Saved" },
                        { to: "/notifications", l: "Notifications" },
                        { to: "/settings", l: "Settings" },
                        ...(user.role === "admin" ? [{ to: "/admin", l: "Admin Panel" }] : [])
                      ].map(({ to, l }) => (
                        <Link key={to} to={to} onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm hover:bg-[var(--bg3)] transition-colors">{l}</Link>
                      ))}
                      <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
                      <button onClick={handleSignOut} className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-[var(--bg3)]" style={{ color: "var(--danger)" }}>Log Out</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/auth/login" className="sq-btn sq-btn-outline text-xs px-3 py-1.5">Log In</Link>
            <Link to="/auth/register" className="sq-btn sq-btn-primary text-xs px-3 py-1.5">Sign Up</Link>
          </div>
        )}
      </div>
    </header>
  )
}

function AvatarFallback({ name }: { name: string }) {
  const c = ["#ff4500", "#ff6534", "#0dd3ff", "#46d369", "#fbbf24"]
  const bg = c[(name || "?").charCodeAt(0) % c.length]
  return (
    <div style={{ width: "100%", height: "100%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".65rem", fontWeight: 900, color: "#fff", fontFamily: "Outfit,sans-serif" }}>
      {(name || "?")[0].toUpperCase()}
    </div>
  )
}
